import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Dropdown } from 'semantic-ui-react';
import FeatherIcon from 'feather-icons-react';
import CustomModal from '../../configuracion/components/customModal';
import { CreateCostCenter, ModifyCostCenter } from '../../../graphql/costCenter';
import { importAlertState } from '../../../redux/actions/alert';
import {
  COST_CENTER_COLORS,
  COST_CENTER_ICONS,
  CostCenterColorOption,
  DEFAULT_COST_CENTER_COLOR,
  DEFAULT_COST_CENTER_ICON,
  getCostCenterColorTokens,
  parseCostCenterHex,
} from './costCenter.constants';
import { filterCostCenterIcons } from './costCenterIconSearch';
import './CostCenterCatalogModal.scss';

const CostCenterCatalogModal = ({
  open,
  onClose,
  companyId,
  mode = 'create',
  initial = null,
  onSuccess,
}) => {
  const { t } = useTranslation('documents');
  const dispatch = useDispatch();
  const { executeMutation: createMany } = CreateCostCenter();
  const { executeMutation: modifyCostCenter } = ModifyCostCenter();

  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_COST_CENTER_COLOR);
  const [icon, setIcon] = useState(DEFAULT_COST_CENTER_ICON);
  const [iconSearch, setIconSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const filteredIcons = useMemo(
    () => filterCostCenterIcons(iconSearch),
    [iconSearch],
  );

  const colorOptions = useMemo(
    () =>
      COST_CENTER_COLORS.map((c) => {
        const label = t(`costCenter.colors.${c.key}`);
        return {
          key: c.hex,
          value: c.hex,
          text: label,
          content: <CostCenterColorOption label={label} hex={c.hex} />,
        };
      }),
    [t],
  );

  const colorTokens = getCostCenterColorTokens(color);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initial) {
      setName(initial.name || '');
      setColor(parseCostCenterHex(initial.color));
      const initialIcon = initial.icon || DEFAULT_COST_CENTER_ICON;
      setIcon(COST_CENTER_ICONS.includes(initialIcon) ? initialIcon : DEFAULT_COST_CENTER_ICON);
    } else {
      setName('');
      setColor(DEFAULT_COST_CENTER_COLOR);
      setIcon(DEFAULT_COST_CENTER_ICON);
    }
    setIconSearch('');
  }, [open, mode, initial]);

  const isValid = name.trim().length > 0;

  const handleSave = async () => {
    if (!companyId || !isValid) return;
    setIsLoading(true);
    try {
      if (mode === 'edit' && initial?.id) {
        const initialColorLabel =
          initial.colorLabel || getCostCenterColorTokens(initial.color).label;
        const fields = [
          { key: 'name', value: name.trim(), oldValue: initial.name },
          { key: 'color', value: color, oldValue: parseCostCenterHex(initial.color) },
          { key: 'colorLabel', value: colorTokens.label, oldValue: initialColorLabel },
          { key: 'icon', value: icon, oldValue: initial.icon },
        ];
        const { error } = await modifyCostCenter({
          companyId,
          input: [{ id: initial.id, fields }],
        });
        if (error) throw error;
        dispatch(importAlertState({ content: t('costCenter.catalog.updated'), state: 'success', duration: 3 }));
      } else {
        const { data, error } = await createMany({
          inputsArr: [{
            companyId,
            name: name.trim(),
            color,
            colorLabel: colorTokens.label,
            icon,
          }],
        });
        if (error) throw error;
        if (!data?.createManyCostCenter?.length) {
          throw new Error(t('costCenter.catalog.createFailed'));
        }
        dispatch(importAlertState({ content: t('costCenter.catalog.created'), state: 'success', duration: 3 }));
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      let msg = err?.message || t('costCenter.catalog.saveError');
      if (msg.startsWith('[GraphQL]')) msg = msg.replace('[GraphQL]', '').trim();
      dispatch(importAlertState({ content: msg, state: 'error', duration: 5 }));
    } finally {
      setIsLoading(false);
    }
  };

  const title = mode === 'edit' ? t('costCenter.catalog.editTitle') : t('costCenter.catalog.createTitle');

  const renderColorLabel = (option) => {
    const hex = parseCostCenterHex(option?.value ?? color);
    const c = getCostCenterColorTokens(hex);
    return <CostCenterColorOption label={c.label} hex={c.hex} />;
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={title}
      classNameModal="cost-center-catalog-modal create-obligation-modal"
      classNameContent="create-obligation-modal-content cost-center-catalog-modal__content scfo-modal-form-body"
      confirmLabel={mode === 'edit' ? t('costCenter.catalog.save') : t('costCenter.catalog.create')}
      confirmDisabled={!isValid}
      isLoading={isLoading}
      onConfirm={handleSave}
      size="large"
      modalStyle={{
        maxWidth: '95%',
        minWidth: '1000px',
        minHeight: '70vh',
        maxHeight: '85vh',
        overflow: 'hidden',
        margin: '0 auto',
      }}
      dimmer={{ style: { zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' } }}
      content={
        <div className="cost-center-catalog-modal__body">
          <div className="cost-center-catalog-modal__left">
            <div className="obligation-section-title">{t('costCenter.catalog.generalInfo')}</div>
            <div className="cost-center-catalog-modal__fields-row">
              <div className="obligation-field">
                <label className="obligation-label" htmlFor="cost-center-name">
                  {t('costCenter.catalog.nameLabel')}
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  id="cost-center-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('costCenter.catalog.namePlaceholder')}
                />
              </div>
              <div className="obligation-field">
                <label className="obligation-label" htmlFor="cost-center-color">
                  {t('costCenter.catalog.colorLabel')}
                </label>
                <Dropdown
                  id="cost-center-color"
                  selection
                  fluid
                  options={colorOptions}
                  value={color}
                  onChange={(_, { value }) => setColor(value)}
                  renderLabel={renderColorLabel}
                  className="cost-center-catalog-modal__color-dropdown"
                />
              </div>
            </div>
          </div>

          <div className="cost-center-catalog-modal__icons">
            <div className="obligation-section-title">{t('costCenter.catalog.iconSection')}</div>
            <p className="hint">
              {t('costCenter.catalog.iconPreview', { color: colorTokens.label })}
            </p>
            <div className="obligation-field cost-center-catalog-modal__icon-search-field">
              <label className="obligation-label" htmlFor="cost-center-icon-search">
                {t('costCenter.catalog.iconSearchLabel')}
              </label>
              <input
                id="cost-center-icon-search"
                type="search"
                value={iconSearch}
                onChange={(e) => setIconSearch(e.target.value)}
                placeholder={t('costCenter.catalog.iconSearchPlaceholder')}
                autoComplete="off"
              />
            </div>
            <div
              className="cost-center-catalog-modal__icon-grid"
              style={{ '--cc-icon-fg': colorTokens.hex }}
            >
              {filteredIcons.length === 0 ? (
                <p className="cost-center-catalog-modal__icon-empty">
                  {t('costCenter.catalog.iconEmpty', { term: iconSearch.trim() })}
                </p>
              ) : (
                filteredIcons.map((iconName) => {
                  const selected = icon === iconName;
                  return (
                    <button
                      key={iconName}
                      type="button"
                      className={`cost-center-catalog-modal__icon-btn ${
                        selected ? 'cost-center-catalog-modal__icon-btn--selected' : ''
                      }`}
                      onClick={() => setIcon(iconName)}
                      aria-label={iconName}
                      aria-pressed={selected}
                      style={selected ? { borderColor: colorTokens.hex } : undefined}
                    >
                      <FeatherIcon icon={iconName} size={22} color={colorTokens.hex} />
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      }
    />
  );
};

export default CostCenterCatalogModal;
